"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VarusReceiptNumberFacts = exports.VARUS_RECEIPT_NUMBER_STATUSES = exports.VARUS_RECEIPT_EXTENSION_KEY = exports.LotteryPayoutMovementFacts = exports.LOTTERY_PAYOUT_WITHDRAWAL_REASON = exports.LOTTERY_PAYOUT_DEPOSIT_REASON = exports.LOTTERY_PAYOUT_EXTENSION_KEY = exports.DocumentFeedReceiptFacts = exports.DocumentFeedVoidReason = exports.DocumentFeedPaymentFacts = exports.DocumentFeedLineFacts = exports.DOCUMENT_FEED_CAPABILITY = exports.DOCUMENT_FEED_FACTS_KEY = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
/**
 * VL-353 — the facts a till attaches to what it uploads so that a store's
 * document feed (integration profile `ip-4`, see `link.ts`) can be built from
 * the upload alone.
 *
 * ------------------------------------------------------------------
 * WHERE THEY TRAVEL
 * ------------------------------------------------------------------
 * In the existing `extensions` maps of `SyncTransactionRecord`, under the key
 * `rl.export`:
 *
 * - `record.extensions['rl.export']` — `DocumentFeedReceiptFacts`;
 * - `record.lines[i].extensions['rl.export']` — `DocumentFeedLineFacts`.
 *
 * A payment leg's own `extensions` is a closed object, so the per-leg facts
 * sit in the receipt block as `payments[]`, each naming its leg by `index`
 * (the position in `record.payments`).
 *
 * ------------------------------------------------------------------
 * READING
 * ------------------------------------------------------------------
 * `safeParse` only. A block that does not parse is treated as ABSENT and
 * noted by the reader; it never refuses a receipt. Back Office stores the
 * maps as it receives them and does not validate these blocks.
 *
 * Every object is `.passthrough()`: a later till may add a key, and a reader
 * built before it must keep the rest. `v` is the block's own version; a
 * reader that does not know a version treats the block as absent.
 *
 * A till attaches the facts — and uploads cancellations and copies its fiscal
 * journal to the Link — only while Back Office advertises the
 * `sync.document-feed` capability to it.
 */
exports.DOCUMENT_FEED_FACTS_KEY = 'rl.export';
exports.DOCUMENT_FEED_CAPABILITY = 'sync.document-feed';
exports.DocumentFeedLineFacts = zod_1.z
    .object({
    v: zod_1.z.literal(1),
    /** The sold quantity in thousandths of `unit` — 456 g is 456, two pieces 2000. */
    quantityThousandths: zod_1.z.number().int().positive().optional(),
    unit: zod_1.z.enum(['pcs', 'kg']).optional(),
    /** The item's catalogue barcode, for a line that was not scanned. */
    barcode: zod_1.z.string().trim().min(1).max(128).optional(),
    /** The store system's tax group number of the item. */
    taxGroup: zod_1.z.number().int().min(0).max(15).optional(),
    /**
     * Reserved (VL-375): the store system's product group of the item
     * (`156` is a State Lottery ticket). No till fills it yet — the Varus
     * catalogue feed carries no product group — so a lottery line is told by
     * `taxGroup` until one does.
     */
    productGroup: zod_1.z.string().trim().min(1).max(32).optional(),
    /** Reserved: the item's UKTZED code. No till fills it yet. */
    uktzed: zod_1.z
        .string()
        .regex(/^[0-9]{4,10}$/)
        .optional(),
})
    .passthrough();
exports.DocumentFeedPaymentFacts = zod_1.z
    .object({
    /** Position of the leg in `record.payments`. */
    index: zod_1.z.number().int().min(0).max(31),
    /** The acquirer's merchant id from the card slip. */
    merchantId: zod_1.z.string().trim().min(1).max(32).optional(),
    /** The card scheme as the acquirer named it, e.g. `VISA`. */
    paymentSystem: zod_1.z.string().trim().min(1).max(64).optional(),
    /**
     * Passed on unmodified. Explicit `null` means "the till knows of none";
     * it is the one fact a till writes as `null` rather than leaving out.
     */
    infoDependencies: zod_1.z.string().nullable().optional(),
})
    .passthrough();
exports.DocumentFeedVoidReason = zod_1.z.enum(['cashier', 'attendant', 'customer', 'timeout']);
exports.DocumentFeedReceiptFacts = zod_1.z
    .object({
    v: zod_1.z.literal(1),
    /** Display name of the record's `cashierId` at the time. */
    cashierName: zod_1.z.string().trim().min(1).max(128).optional(),
    /**
     * The loyalty ACCOUNT number the CRM returned for the card. Never the
     * record's `loyaltyCardId`; null or absent when the CRM sent none.
     */
    cardAccountNumber: zod_1.z.string().trim().min(1).max(128).nullable().optional(),
    /** Immutable receipt-origin evidence; absent for historical/unknown captures. */
    purchaseChannelCode: zod_1.z.number().int().min(1).max(5).optional(),
    bonuses: zod_1.z
        .object({
        accruedMinor: pricing_js_1.MoneyMinor.nullable().optional(),
        /** Balances at identification, before this receipt. */
        availableMinor: pricing_js_1.MoneyMinor.nullable().optional(),
        availableCashMinor: pricing_js_1.MoneyMinor.nullable().optional(),
    })
        .passthrough()
        .optional(),
    payments: zod_1.z.array(exports.DocumentFeedPaymentFacts).max(32).optional(),
    /** Present on a cancellation (`receiptType: 'void'`) only. */
    void: zod_1.z
        .object({
        /** When the cancelled receipt was opened. */
        openedAt: zod_1.z.string().datetime().optional(),
        reason: exports.DocumentFeedVoidReason,
        approverCashierId: zod_1.z.string().min(1).max(64).optional(),
    })
        .passthrough()
        .optional(),
})
    .passthrough();
/**
 * VL-374 / VL-375 — the block a Varus till puts on each drawer movement of a
 * State Lottery prize payout, under `payload.extensions['rl.lottery.payout']`
 * of a `cash-movement` shift event.
 *
 * One payout is booked as a pair at one instant: the deposit (`operation`
 * 13, a `paid_in` with reason `lottery_payout_deposit`) and the withdrawal
 * (`operation` 14, a `paid_out` with reason `lottery_payout`). Both carry the
 * same `pairId` and the `<Winner>` tags in `userInfo`.
 *
 * `CashMovementPayload` does not declare `extensions`, so Back Office never
 * keeps this block (it holds a ticket's verification code). The store Link
 * reads it from the raw event and keeps it for the document feed.
 */
exports.LOTTERY_PAYOUT_EXTENSION_KEY = 'rl.lottery.payout';
exports.LOTTERY_PAYOUT_DEPOSIT_REASON = 'lottery_payout_deposit';
exports.LOTTERY_PAYOUT_WITHDRAWAL_REASON = 'lottery_payout';
exports.LotteryPayoutMovementFacts = zod_1.z
    .object({
    version: zod_1.z.number().int().min(1),
    /** The store system's receipt type the movement stands for. */
    operation: zod_1.z.union([zod_1.z.literal(13), zod_1.z.literal(14)]),
    pairId: zod_1.z.string().min(1).max(64),
    pairedMovementId: zod_1.z.string().min(1).max(64).optional(),
    checkId: zod_1.z.union([zod_1.z.string().max(64), zod_1.z.number().int()]).optional(),
    lotoBarcode: zod_1.z.string().max(128).nullable().optional(),
    ticketBarcode: zod_1.z.string().max(128).nullable().optional(),
    payoutMinor: zod_1.z.number().int().positive().optional(),
    /** `<Winner><BarCode>…</BarCode><Scan>…</Scan><Verific>…</Verific></Winner>`. */
    userInfo: zod_1.z.string().max(1024).nullable().optional(),
    /**
     * The register's number of the payout's fiscal (return) receipt. Never
     * the movement's `CheckID` / `receipt_number` (VL-375): that is
     * `receiptNumber`.
     */
    fiscalReceiptNo: zod_1.z
        .union([zod_1.z.string().max(64), zod_1.z.number().int().nonnegative()])
        .nullable()
        .optional(),
    /**
     * VL-374 / VL-375: the till's own receipt number of THIS movement's store
     * receipt (13 or 14 — the two halves carry two distinct numbers), or null
     * with `receiptNumberStatus` for a payout recorded before the till
     * numbered payouts. Declared `unknown` on purpose: a value that is not a
     * usable number must not make the whole block unreadable (that would be
     * reported as an inconsistent payout); it is judged, and held, by
     * `tillReceiptNumberOfPayout` in `@reform-lite/connectors`.
     */
    receiptNumber: zod_1.z.unknown().optional(),
    receiptNumberStatus: zod_1.z.unknown().optional(),
})
    .passthrough();
/**
 * VL-374 / VL-375 / VL-376 — the till's own receipt number, on the record of
 * a sale or a return under `record.extensions['rl.varus']`.
 *
 * `receiptNumber` is the number the store system knows the receipt by: the
 * `CheckID` / `receipt_number` of every message and store-system row built
 * from it. It is the till's continuous receipt counter — never the fiscal
 * number, a UUID or a clock value. A till that has no number for a receipt
 * sends `receiptNumber: null` and says why in `receiptNumberStatus`
 * ([VARUS_RECEIPT_NUMBER_STATUSES]); such a receipt is HELD by every
 * consumer, never given a number of the consumer's own.
 *
 * `.passthrough()`, and optional on the record: an upload from a till that
 * predates the block still parses; it carries no number, so it is held.
 */
exports.VARUS_RECEIPT_EXTENSION_KEY = 'rl.varus';
/** Why a till uploaded a receipt without its number. */
exports.VARUS_RECEIPT_NUMBER_STATUSES = [
    /** Held before numbering existed, or a till without the counter. */
    'unnumbered',
    /** The counter could not be read or written when the receipt was uploaded. */
    'unavailable',
    /** A prize payout recorded before the till numbered payouts. */
    'legacy_unnumbered',
];
exports.VarusReceiptNumberFacts = zod_1.z
    .object({
    v: zod_1.z.literal(1),
    /** Judged by the consumer (integer, positive, within its column); see above. */
    receiptNumber: zod_1.z.unknown().optional(),
    receiptNumberStatus: zod_1.z.unknown().optional(),
})
    .passthrough();
//# sourceMappingURL=document-feed.js.map